(() => {
  const CONFIG_SELECTOR = 'script[id^="WallArtGalleryRendererConfig-"]';
  const GLOBAL_STATE_KEY = '__productSpecsDynamicSizeV1';
  const state = window[GLOBAL_STATE_KEY] || (window[GLOBAL_STATE_KEY] = { instances: new Map() });
  const FRAMED_TYPES = new Set(['framed_art_print', 'framed_canvas_print']);
  const FRAME_THICKNESS_INCH = 1;

  function getSectionIdFromConfigScript(configScript) {
    const id = String(configScript?.id || '');
    const prefix = 'WallArtGalleryRendererConfig-';
    if (!id.startsWith(prefix)) return '';
    return id.slice(prefix.length).trim();
  }

  function parseConfig(configScript) {
    if (!configScript) return null;
    const raw = String(configScript.textContent || '').trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (_error) {
      return null;
    }
  }

  function findProductRoot(configScript, sectionId) {
    if (configScript?.closest) {
      const closest = configScript.closest('product-info');
      if (closest) return closest;
    }

    if (sectionId) {
      const byDataSection = document.querySelector(`product-info[data-section="${sectionId}"]`);
      if (byDataSection) return byDataSection;

      const byId = document.querySelector(`product-info#MainProduct-${sectionId}`);
      if (byId) return byId;
    }

    return null;
  }

  function normalizeProductType(value) {
    const key = String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[_-]+/g, ' ');

    if (!key) return 'art_print';
    if (key.includes('framed canvas') || key === 'framed_canvas_print') return 'framed_canvas_print';
    if (key.includes('framed art print') || key.includes('framed print') || key === 'framed_art_print')
      return 'framed_art_print';
    if (key.includes('canvas print') || key === 'canvas' || key === 'canvas_print') return 'canvas_print';
    if (key.includes('art print') || key.includes('poster') || key.includes('print') || key === 'art_print')
      return 'art_print';
    return 'art_print';
  }

  function parseSize(sizeValue) {
    const normalized = String(sizeValue || '')
      .toLowerCase()
      .replace(/inches|inch|in\.|in/g, '')
      .replace(/"/g, '')
      .replace(/″/g, '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, ' ')
      .trim();

    const match = normalized.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!match) return null;

    const width = Number.parseFloat(match[1]);
    const height = Number.parseFloat(match[2]);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;

    return { width, height };
  }

  function formatInchValue(value) {
    if (!Number.isFinite(value)) return '';
    if (Number.isInteger(value)) return String(value);
    return String(Math.round(value * 100) / 100).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
  }

  function formatSizeString(sizeObject) {
    if (!sizeObject) return '';
    const width = formatInchValue(sizeObject.width);
    const height = formatInchValue(sizeObject.height);
    if (!width || !height) return '';
    // No space around `x` — matches the wall-art-picker display
    // convention (`24"x36"`) so spec rows + picker label read the same.
    return `${width}"x${height}"`;
  }

  function getVariantIdFromProductRoot(productRoot) {
    const input = productRoot?.querySelector('form input[name="id"]');
    const raw = String(input?.value || '').trim();
    if (!raw) return '';
    return raw;
  }

  function readSelectedVariantDataNode(productRoot) {
    const node = productRoot?.querySelector('variant-selects script[data-selected-variant]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_error) {
      return null;
    }
  }

  function resolveVariant(config, productRoot) {
    const variants = Array.isArray(config?.variants) ? config.variants : [];
    if (!variants.length) return null;

    const variantId = getVariantIdFromProductRoot(productRoot);
    if (variantId) {
      const byId = variants.find((variant) => String(variant?.id) === variantId);
      if (byId) return byId;
    }

    const selectedVariantData = readSelectedVariantDataNode(productRoot);
    if (selectedVariantData?.id) {
      const byDataNode = variants.find((variant) => String(variant?.id) === String(selectedVariantData.id));
      if (byDataNode) return byDataNode;
    }

    return variants.find((variant) => variant?.available) || variants[0] || null;
  }

  function setRowVisibility(rows, isVisible) {
    rows.forEach((row) => {
      if (!row) return;
      if (isVisible) {
        row.hidden = false;
        row.removeAttribute('hidden');
      } else {
        row.hidden = true;
        row.setAttribute('hidden', '');
      }
    });
  }

  function setRowValues(valueNodes, valueText) {
    valueNodes.forEach((node) => {
      if (!node) return;
      node.textContent = valueText;
    });
  }

  function updateDynamicSpecsRows(config, productRoot, pickerState) {
    const artworkRows = Array.from(productRoot.querySelectorAll('[data-dynamic-artwork-size-row]'));
    const artworkValues = Array.from(productRoot.querySelectorAll('[data-dynamic-artwork-size-value]'));
    const finishedRows = Array.from(productRoot.querySelectorAll('[data-dynamic-finished-size-row]'));
    const finishedValues = Array.from(productRoot.querySelectorAll('[data-dynamic-finished-size-value]'));
    if (!artworkRows.length && !finishedRows.length) return;

    // Source-of-truth precedence (most-authoritative first):
    //   1. pickerState  — set by `wallart:selection:changed`. The
    //      picker speaks in canonical tokens AND knows exactly what
    //      the customer just clicked. Always the right answer when set.
    //   2. variant.size / variant.productType from the resolved
    //      Shopify variant — used for initial render before the
    //      picker has fired (or for non-picker products).
    //   3. config.productType — last-resort fallback baked into
    //      the WallArtGalleryRendererConfig payload.
    const variant = resolveVariant(config, productRoot);
    const pickerSize = pickerState && pickerState.size ? parseSize(pickerState.size) : null;
    const size = pickerSize || parseSize(variant?.size);

    // Without a valid size we can't fill either row — hide both
    // rather than leaving them showing stale values from a prior render.
    if (!size) {
      setRowValues(artworkValues, '');
      setRowVisibility(artworkRows, false);
      setRowValues(finishedValues, '');
      setRowVisibility(finishedRows, false);
      return;
    }

    setRowValues(artworkValues, formatSizeString(size));
    setRowVisibility(artworkRows, true);

    // Finished Size — always shown, with two rules:
    //   * Framed (framed_art_print / framed_canvas_print): artwork +
    //     FRAME_THICKNESS_INCH on EACH dimension. The frame profile
    //     overlaps ~½" on each side, so a 24×36 artwork sits inside a
    //     25×37 outer frame (the merchant's spec).
    //   * Unframed (art_print / canvas_print): same as artwork.
    const productType = normalizeProductType(
      (pickerState && pickerState.format) ||
      variant?.productType ||
      config?.productType
    );
    const isFramed = FRAMED_TYPES.has(productType);
    const finishedSize = isFramed
      ? { width: size.width + FRAME_THICKNESS_INCH, height: size.height + FRAME_THICKNESS_INCH }
      : { width: size.width, height: size.height };
    setRowValues(finishedValues, formatSizeString(finishedSize));
    setRowVisibility(finishedRows, true);
  }

  function initSection(configScript) {
    const sectionId = getSectionIdFromConfigScript(configScript);
    if (!sectionId) return;

    const existingInstance = state.instances.get(sectionId);
    if (existingInstance && typeof existingInstance.destroy === 'function') {
      existingInstance.destroy();
    }

    const config = parseConfig(configScript);
    if (!config) return;

    const productRoot = findProductRoot(configScript, sectionId);
    if (!productRoot) return;

    const hasDynamicSpecsRows =
      productRoot.querySelector('[data-dynamic-artwork-size-row]') ||
      productRoot.querySelector('[data-dynamic-finished-size-row]');
    if (!hasDynamicSpecsRows) return;

    const abortController = new AbortController();
    const signal = abortController.signal;
    let frameRequest = null;

    // Cache of the latest picker selection (set by the
    // `wallart:selection:changed` listener below). Used as the
    // authoritative source for productType + size when present —
    // the picker speaks in canonical snake-case tokens
    // (`framed_canvas_print`, `24x36`) that bypass any variant-data
    // gaps. Falls back to variant lookup for the initial render
    // (before the picker has fired its first commit).
    const pickerState = { format: null, size: null, frameColor: null };

    const scheduleUpdate = () => {
      if (frameRequest != null) {
        window.cancelAnimationFrame(frameRequest);
      }
      frameRequest = window.requestAnimationFrame(() => {
        frameRequest = null;
        updateDynamicSpecsRows(config, productRoot, pickerState);
      });
    };

    updateDynamicSpecsRows(config, productRoot, pickerState);

    productRoot.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const isVariantInput =
          target.matches('variant-selects select') ||
          target.matches('variant-selects input[type="radio"]') ||
          target.matches('form input[name="id"]');
        if (!isVariantInput) return;
        scheduleUpdate();
      },
      { signal }
    );

    let unsubscribeVariantChange = null;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
        if (String(payload?.data?.sectionId ?? '') !== String(sectionId)) return;
        scheduleUpdate();
      });
    }

    // The wall-art-picker bypasses Dawn's native variant-selects (it
    // sets form `input[name="id"].value` directly, which doesn't auto-
    // fire `change`, and it doesn't publish to PUB_SUB_EVENTS.variantChange).
    // Without this listener the dynamic spec rows would only render once,
    // on initial load, and never update when the customer picks a new
    // size/format in the picker. The event bubbles up through DOM, so
    // a window-level listener catches every picker on the page.
    //
    // We ALSO capture the picker's format + size into `pickerState`
    // before scheduling — used as the authoritative source in
    // updateDynamicSpecsRows below, since the variant payload's
    // productType field isn't reliably populated for picker-driven
    // products (the picker's format is the canonical source).
    window.addEventListener(
      'wallart:selection:changed',
      (event) => {
        const detail = event && event.detail;
        if (detail) {
          if (detail.format)      pickerState.format     = String(detail.format);
          if (detail.size)        pickerState.size       = String(detail.size);
          if (detail.frameColor)  pickerState.frameColor = String(detail.frameColor);
        }
        scheduleUpdate();
      },
      { signal }
    );

    // Picker may have already committed BEFORE this script's init()
    // runs (assets load with `defer`, order between picker + this
    // script isn't guaranteed). Seed pickerState from the window
    // global the picker writes on every commit.
    const initialPickerState = window.__wallArtCurrentSelection;
    if (initialPickerState) {
      if (initialPickerState.format)     pickerState.format     = String(initialPickerState.format);
      if (initialPickerState.size)       pickerState.size       = String(initialPickerState.size);
      if (initialPickerState.frameColor) pickerState.frameColor = String(initialPickerState.frameColor);
      // Re-run with the seeded state.
      scheduleUpdate();
    }

    const destroy = () => {
      abortController.abort();
      if (frameRequest != null) {
        window.cancelAnimationFrame(frameRequest);
        frameRequest = null;
      }
      if (typeof unsubscribeVariantChange === 'function') {
        unsubscribeVariantChange();
      }
      if (state.instances.get(sectionId)?.destroy === destroy) {
        state.instances.delete(sectionId);
      }
    };

    state.instances.set(sectionId, { destroy });
  }

  function initAll(root = document) {
    const scripts = new Set();
    if (root instanceof Element && root.matches(CONFIG_SELECTOR)) {
      scripts.add(root);
    }
    if (root && typeof root.querySelectorAll === 'function') {
      root.querySelectorAll(CONFIG_SELECTOR).forEach((script) => scripts.add(script));
    }

    scripts.forEach((script) => {
      try {
        initSection(script);
      } catch (_error) {
        // Fail silently to avoid blocking product page behavior.
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event?.target instanceof Element ? event.target : document;
    initAll(sectionRoot);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = String(event?.detail?.sectionId || '').trim();
    if (!sectionId) return;
    const instance = state.instances.get(sectionId);
    if (instance && typeof instance.destroy === 'function') {
      instance.destroy();
    }
  });
})();
